import { useState } from 'react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { PicassoFrame } from '../components/PicassoFrame'
import { MODE_ICON, MODE_LABEL } from '../components/modes'
import { useDeletePicassoSession, usePicassoSessions } from '../hooks/usePicasso'

import type { PicassoId } from '../api/picassoApi'

const PAGE = 50

export function PicassoSessionsPage() {
  const [mine, setMine] = useState(false)
  const [offset, setOffset] = useState(0)
  const { data, isLoading, isFetching } = usePicassoSessions({ mine, limit: PAGE, offset })
  const del = useDeletePicassoSession()
  const [confirm, setConfirm] = useState<PicassoId | null>(null)
  const total = data?.total ?? 0

  return (
    <PicassoFrame
      title="Your sessions"
      description="Every session run from the search console: what was asked, in which mode, and whether anything failed. Open one to replay it turn by turn, with its graph and trace."
      actions={<Button size="sm" asChild><Link to="/app/picasso"><Plus className="mr-1.5 h-3.5 w-3.5" /> New session</Link></Button>}
    >
      <div className="flex gap-2">
        {[{ v: false, l: 'Everyone' }, { v: true, l: 'Only mine' }].map((o) => (
          <button
            key={o.l}
            onClick={() => { setMine(o.v); setOffset(0) }}
            className={`rounded-full border px-3 py-1 text-sm ${mine === o.v ? 'bg-slate-900 text-white' : 'bg-white hover:bg-slate-50'}`}
          >
            {o.l}
          </button>
        ))}
      </div>
      <Card>
        <CardContent className={`p-0 ${isFetching ? 'opacity-70' : ''}`}>
          {isLoading ? <Skeleton className="m-4 h-64" /> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Session</TableHead>
                  <TableHead>Asked</TableHead>
                  <TableHead>Modes</TableHead>
                  <TableHead className="text-right">Turns</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead>Last turn</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.data ?? []).map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="max-w-[16rem]">
                      <Link to={`/app/picasso/s/${s.id}`} className="font-medium hover:underline">{s.title}</Link>
                    </TableCell>
                    <TableCell className="max-w-[24rem] text-xs text-slate-500">
                      <span className="line-clamp-2">{s.preview.join(' → ')}{s.turns > s.preview.length ? ' → …' : ''}</span>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex gap-1.5">
                        {s.modes.map((m) => {
                          const Icon = MODE_ICON[m]
                          return <span key={m} title={MODE_LABEL[m]}><Icon className="h-3.5 w-3.5 text-slate-500" /></span>
                        })}
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {s.turns}
                      {s.errors > 0 && <span className="ml-1 text-xs text-rose-700">({s.errors} failed)</span>}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{s.createdByEmail ?? '—'}</TableCell>
                    <TableCell className="text-xs text-slate-500">{formatDistanceToNow(new Date(s.updatedAt), { addSuffix: true })}</TableCell>
                    <TableCell className="text-right">
                      {confirm === s.id ? (
                        <span className="inline-flex gap-1">
                          <Button size="sm" variant="destructive" disabled={del.isPending} onClick={() => del.mutate(s.id, { onSettled: () => setConfirm(null) })}>Delete</Button>
                          <Button size="sm" variant="ghost" onClick={() => setConfirm(null)}>Keep</Button>
                        </span>
                      ) : (
                        <button onClick={() => setConfirm(s.id)} className="text-slate-400 hover:text-rose-700" title="Delete session"><Trash2 className="h-4 w-4" /></button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!data?.data.length && (
                  <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-slate-500">No sessions yet. <Link to="/app/picasso" className="underline">Start one</Link>.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
          {total > PAGE && (
            <div className="flex items-center justify-between p-3 text-sm text-slate-500">
              <span>{offset + 1}–{Math.min(offset + PAGE, total)} of {total}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={offset === 0} onClick={() => setOffset(offset - PAGE)}>Previous</Button>
                <Button size="sm" variant="outline" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </PicassoFrame>
  )
}
